import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip } from '../../components/ui/Chip';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  dealDetailQuery, dealOwnersQuery, dealClientsQuery, dealContractsQuery, dealProposalQuery,
  CANONE_MENSILE, CONTRIBUTO, FEE_ISTRUTTORIA, REQUISITI, REQ_LABEL,
  SCENARI, SCENARIO_LABEL, SPESA, SUCCESS_FEE_RATE,
  canWriteDeals, counterparty, dueInfo, fullName,
  type DealFull, type ReqStatusKey, type ScenarioKey, type StageLite,
} from './dealsApi';
import { useDealActions } from './useDealActions';
import { ClosePopover } from './ClosePopover';
import { MovePipelinePopover } from './MovePipelinePopover';
import { NextActionPopover } from './NextActionPopover';
import { RenewPopover } from './RenewPopover';

/**
 * Dettaglio trattativa — una colonna, righe chiave-valore nude, stesso disegno
 * del dettaglio task. Ogni campo salva al cambio (PATCH parziale).
 *
 * L'ordine dice cosa conta: in quale pipeline sta e a che punto è, quanto vale,
 * cosa manca per andare avanti (sul voucher i tre requisiti del bando), cosa si
 * fa adesso, e infine lo storico. In evidenza ci sono due azioni sole: lo step
 * vinto della pipeline e Chiudi.
 */
export function DealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteDeals(user);
  const { update, win, close, movePipeline, nextAction, addEvent, remove } = useDealActions();
  const [closing, setClosing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [planning, setPlanning] = useState(false);

  const { data: deal, isLoading, isError } = useQuery({ ...dealDetailQuery(id ?? ''), enabled: !!id, retry: false });
  const { data: owners } = useQuery(dealOwnersQuery);
  const { data: contracts } = useQuery(dealContractsQuery);
  const { data: clientsData } = useQuery(dealClientsQuery);

  if (!id) return <Navigate to="/trattative" replace />;
  if (isError) {
    return (
      <div className="page">
        <Breadcrumb items={[{ label: 'Trattative', to: '/trattative' }, { label: 'Dettaglio' }]} />
        <div className="max-w-[480px] mx-auto py-20 text-center">
          <h1 className="t-h1">Trattativa non trovata</h1>
          <div className="mute mt-2">Forse è stata eliminata, o il link è sbagliato.</div>
        </div>
      </div>
    );
  }
  if (isLoading || !deal) return <DetailSkeleton header="chips" kpis={0} cards={1} />;

  const azienda = counterparty(deal);
  const patch = (data: Record<string, unknown>) => update.mutate({ id: deal.id, data });
  const prevista = dueInfo(deal.expectedCloseDate, !deal.isOpen);
  const openStages = deal.stages.filter((s) => s.outcome !== 'PERSO');
  const lostStages = deal.stages.filter((s) => s.outcome === 'PERSO');
  const wonStage = deal.stages.find((s) => s.outcome === 'VINTO');

  const onDelete = async () => {
    const r = await remove.mutateAsync({ id: deal.id, title: azienda });
    if (r) navigate('/trattative');
  };

  return (
    // m-detail: sul telefono la scheda è una colonna sola e le griglie a due
    // colonne collassano da sole. Sopra i 768px la classe non ha nessuna regola.
    <div className="page m-detail">
      <Breadcrumb items={[{ label: 'Trattative', to: '/trattative' }, { label: azienda }]} />

      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px] flex-1">
          <TitleEditor title={deal.title} canWrite={canWrite} onSave={(title) => patch({ title })} />
          {/* whitespace-nowrap su ogni coppia: sul telefono la riga va a capo, e
              senza questo si spezzava dentro la coppia ("Ultimo" su una riga,
              "movimento oggi" su quella dopo). Sul computer la riga è tagliata
              da truncate e non va a capo comunque: lì non cambia niente. */}
          <div className="detail-meta mt-2 truncate">
            <span className="whitespace-nowrap"><span className="mute-2 mr-1.5">Pipeline</span><span className="v">{deal.pipeline.name}</span></span>
            {deal.owner && <><span className="mute-2">·</span><span className="whitespace-nowrap"><span className="mute-2 mr-1.5">Segue</span><span className="v">{fullName(deal.owner)}</span></span></>}
            <span className="mute-2">·</span>
            <span className="whitespace-nowrap"><span className="mute-2 mr-1.5">Ultimo movimento</span><span className="v mono">{deal.daysSinceLastEvent === 0 ? 'oggi' : `${deal.daysSinceLastEvent} g fa`}</span></span>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2.5 items-center">
            <Chip tone={deal.isClosed ? 'ghost' : deal.isWon ? 'ok' : 'info'} dot>{deal.stage.name}</Chip>
            {deal.fascia && <Chip tone="ghost">Fascia {deal.fascia}</Chip>}
            {deal.expectedCloseDate && (
              <Chip tone={prevista.tone === 'alert' ? 'alert' : prevista.tone === 'warn' ? 'warn' : 'ghost'}>
                <Icon name="calendar" size={10} /> {prevista.tone === 'mute' ? fmtDate(deal.expectedCloseDate) : prevista.label}
              </Chip>
            )}
            {deal.estendibile && <Chip tone="info">Può estendere a 40k entro tre mesi</Chip>}
            {deal.lostReason && <Chip tone="ghost">{deal.lostReason}</Chip>}
          </div>
        </div>

        <div className="flex gap-2 pb-1 shrink-0 relative">
          {/* d-only: sul telefono questa stessa azione sta nella barra in fondo.
              "Chiudi" e il menù Azioni restano invece qui, perché aprono un
              popover ancorato al bottone: spostati in fondo si aprirebbero in
              cima alla pagina, fuori dallo schermo. */}
          {canWrite && wonStage && !deal.isWon && (
            <button
              className="btn btn-primary d-only"
              disabled={win.isPending}
              onClick={() => win.mutate({ id: deal.id, title: azienda, stageId: wonStage.id, stageName: wonStage.name, prevStageId: deal.stageId })}
            >
              <Icon name="check" size={12} /> {wonStage.name}
            </button>
          )}
          {canWrite && !deal.isClosed && !!lostStages.length && (
            <button className="btn" disabled={close.isPending} onClick={() => setClosing(true)}>
              <Icon name="x" size={12} /> Chiudi
            </button>
          )}
          <ActionsMenu
            variant="ghost"
            label="Azioni"
            items={[
              // Chi segue non è più una riga della scheda: si cambia da qui
              ...(owners ?? [])
                .filter((u) => u.id !== deal.ownerId)
                .map((u) => ({
                  key: `own-${u.id}`,
                  label: `Passa a ${fullName(u)}`,
                  icon: 'user' as const,
                  hidden: !canWrite,
                  onClick: () => patch({ ownerId: u.id }),
                })),
              { key: 'move', label: 'Sposta in un\'altra pipeline', icon: 'swap', hidden: !canWrite, onClick: () => setMoving(true) },
              { key: 'delete', label: 'Elimina', icon: 'trash', danger: true, hidden: !canWrite, onClick: onDelete },
            ]}
            disabled={remove.isPending}
          />
          {closing && (
            <ClosePopover
              title={azienda}
              stages={lostStages}
              pending={close.isPending}
              onClose={() => setClosing(false)}
              onConfirm={(v) => { setClosing(false); close.mutate({ id: deal.id, title: azienda, prevStageId: deal.stageId, ...v }); }}
            />
          )}
          {moving && (
            <MovePipelinePopover
              title={azienda}
              currentPipelineId={deal.pipelineId}
              pending={movePipeline.isPending}
              onClose={() => setMoving(false)}
              onConfirm={(v) => { setMoving(false); movePipeline.mutate({ id: deal.id, title: azienda, ...v }); }}
            />
          )}
        </div>
      </div>

      <div className="tk-detail">
        <div className="data-hd">
          <span className="t-micro">A che punto è</span>
          {update.isPending && <span className="mute text-[12px]">Salvo…</span>}
        </div>
        <div className="ed-kv">
          <KvRow label="Step">
            {canWrite ? (
              <select className="inp tk-inline" value={deal.stageId} onChange={(e) => patch({ stageId: e.target.value })} disabled={deal.isClosed}>
                {(deal.isClosed ? deal.stages : openStages).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            ) : deal.stage.name}
          </KvRow>
          {/* Probabilità, chi segue e fascia sono spariti dalla scheda (Lorenzo,
              19/09/2026: troppi dati). La probabilità la mette lo step, chi segue
              si legge in testa alla pagina e si cambia dal menu delle azioni. */}
          <KvRow label="Chiusura prevista" hint="quando pensiamo di chiuderla">
            {canWrite
              ? <input className="inp tk-inline mono" type="date" value={deal.expectedCloseDate ?? ''} onChange={(e) => patch({ expectedCloseDate: e.target.value || null })} />
              : fmtDate(deal.expectedCloseDate)}
          </KvRow>
          <KvRow
            label="Rinnovo di"
            hint={deal.renewalOf
              ? `oggi vale € ${fmtEur(deal.renewalOf.annuo, true)} all'anno${deal.renewalOf.endDate ? `, scade il ${fmtDate(deal.renewalOf.endDate)}` : ''}`
              : 'un contratto che sta per scadere, se non è denaro nuovo'}
          >
            {canWrite ? (
              <select
                className="inp tk-inline"
                value={deal.renewalOf?.id ?? ''}
                onChange={(e) => patch({ renewalOfId: e.target.value || null })}
              >
                <option value="">Non è un rinnovo</option>
                {(contracts ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · € {fmtEur(c.annuo, true)}/anno
                  </option>
                ))}
              </select>
            ) : (deal.renewalOf ? deal.renewalOf.name : '—')}
          </KvRow>
        </div>

        {/* Il confine col progetto: compare solo quando la trattativa è vinta.
            Finché è in corso la scheda resta esattamente com'era. */}
        <DopoIlSiBlock deal={deal} canWrite={canWrite} />

        <div className="ed-divider" />

        <ValoreBlock deal={deal} canWrite={canWrite} onPatch={patch} />

        {deal.isVoucher && (
          <>
            <div className="ed-divider" />
            <div className="data-hd"><span className="t-micro">Requisiti del bando</span></div>
            <div className="ed-kv">
              {REQUISITI.map((r) => (
                <KvRow key={r.key} label={r.label} hint={r.hint}>
                  {canWrite ? (
                    <div className="seg-control tk-seg-sm">
                      {(['OK', 'MANCA', 'DA_VERIFICARE'] as ReqStatusKey[]).map((s) => (
                        <button key={s} aria-pressed={deal[r.key] === s} onClick={() => { if (deal[r.key] !== s) patch({ [r.key]: s }); }}>
                          {REQ_LABEL[s]}
                        </button>
                      ))}
                    </div>
                  ) : REQ_LABEL[deal[r.key]]}
                </KvRow>
              ))}
            </div>
            {!deal.requisitiOk && (
              <div className="adm-note mt-3">
                <Icon name="alert" size={12} className="mute" />
                <span>Finché manca qualcosa non si può dire <strong>requisiti verificati</strong>: da sistemare {deal.requisitiMancanti.join(', ')}.</span>
              </div>
            )}
          </>
        )}

        <div className="ed-divider" />

        <div className="data-hd">
          <span className="t-micro">Prossima azione</span>
          {canWrite && !deal.isClosed && (
            <div className="relative">
              <button className="btn btn-sm" onClick={() => setPlanning(true)}>
                <Icon name="calendarPlus" size={12} /> Programma
              </button>
              {planning && (
                <NextActionPopover
                  company={azienda}
                  pending={nextAction.isPending}
                  onClose={() => setPlanning(false)}
                  onConfirm={(v) => { setPlanning(false); nextAction.mutate({ id: deal.id, ...v }); }}
                />
              )}
            </div>
          )}
        </div>
        {deal.tasks.length ? (
          <div className="flex flex-col gap-1.5">
            {deal.tasks.map((t) => {
              const d = dueInfo(t.dueDate, t.status === 'DONE');
              return (
                <div key={t.id} className="row between py-1.5 border-b border-line last:border-0 cursor-pointer" onClick={() => navigate(`/tasks/${t.id}`)}>
                  <div className="row min-w-0" style={{ gap: 8 }}>
                    <Icon name={t.status === 'DONE' ? 'check' : 'tasks'} size={12} className="mute" />
                    <span className={`truncate ${t.status === 'DONE' ? 'line-through mute' : ''}`}>{t.title}</span>
                    {t.assignee && <span className="mute text-[12px]">· {t.assignee.firstName}</span>}
                  </div>
                  <span className={`mono text-[12px] ${d.tone === 'alert' ? 'text-alert font-semibold' : d.tone === 'warn' ? 'text-warn' : 'mute'}`}>{d.label}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mute text-[13px]">Nessuna azione in programma: nessuno sa chi deve chiamare questo cliente.</div>
        )}

        <div className="ed-divider" />

        <ContattoBlock deal={deal} canWrite={canWrite} onPatch={patch} clients={clientsData?.items ?? []} />

        <div className="ed-divider" />

        <StoricoBlock deal={deal} canWrite={canWrite} onAdd={(text) => addEvent.mutate({ id: deal.id, kind: 'NOTA', text })} pending={addEvent.isPending} />
      </div>

      {/* Telefono: l'azione principale della scheda, appoggiata sopra la barra
          di navigazione. È una mutazione diretta, senza popover: per questo può
          stare in fondo senza problemi. */}
      {canWrite && wonStage && !deal.isWon && (
        <div className="m-actionbar m-only">
          <button
            className="btn btn-primary"
            disabled={win.isPending}
            onClick={() => win.mutate({ id: deal.id, title: azienda, stageId: wonStage.id, stageName: wonStage.name, prevStageId: deal.stageId })}
          >
            <Icon name="check" size={12} /> {wonStage.name}
          </button>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Dopo il sì: cosa nasce da questa trattativa
//
// Il confine fra trattativa e progetto è il sì del cliente. Superato quello,
// il sistema PROPONE — non obbliga: si può vincere senza che nasca una
// commessa, perché un canone venduto è una sottoscrizione, non un progetto.
//
// La proposta non è una sola, e qui sta il punto:
//  - pipeline normale → si apre il progetto, e quel valore esce dal potenziale;
//  - RINNOVO → si prolunga il canone che c'è già. Aprire un progetto nuovo
//    conterebbe due volte gli stessi soldi;
//  - VOUCHER → insieme al progetto nasce anche il canone di gestione pratica,
//    120 € al mese per i mesi a preventivo: su quindici trattative sono 72.360 €
//    che altrimenti nessuno registra da nessuna parte.
// ════════════════════════════════════════════════════════════════════
function DopoIlSiBlock({ deal, canWrite }: { deal: DealFull; canWrite: boolean }) {
  const navigate = useNavigate();
  const { renew } = useDealActions();
  const [renewing, setRenewing] = useState(false);
  const openModal = useModalStore((s) => s.open);

  // Finché non è vinta non c'è niente da proporre: non si chiede nemmeno
  const { data: proposta } = useQuery({
    ...dealProposalQuery(deal.id),
    enabled: deal.isWon && !deal.project,
  });

  if (!deal.isWon) return null;

  // Già nato: una riga sola che dice a che punto è la consegna
  if (deal.project) {
    const p = deal.project;
    return (
      <>
        <div className="ed-divider" />
        <div className="data-hd"><span className="t-micro">Ne è nato</span></div>
        <div
          className="row between py-2 cursor-pointer"
          style={{ gap: 12 }}
          onClick={() => navigate(`/projects/${p.id}`)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/projects/${p.id}`); }}
        >
          <div className="row min-w-0" style={{ gap: 8 }}>
            <Icon name="cube" size={12} className="mute" />
            <span className="truncate">{p.name}</span>
            <span className="mono mute-2 text-[12px]">{p.code}</span>
            <Chip tone="ok" dot>{p.phase}</Chip>
          </div>
          <span className="mono text-[12px] mute shrink-0">
            {p.deadline ? `consegna ${fmtDate(p.deadline)}` : 'senza data di consegna'}
          </span>
        </div>
        <div className="mute text-[12px]">
          Da quando il progetto esiste, questo valore non è più potenziale commerciale: sta nel progetto.
        </div>
      </>
    );
  }

  if (!proposta) return null;

  return (
    <>
      <div className="ed-divider" />
      <div className="data-hd">
        <span className="t-micro">{proposta.azione === 'RINNOVO' ? 'Il contratto da prolungare' : 'E adesso'}</span>
        {canWrite && proposta.azione === 'PROGETTO' && (
          <button
            className="btn btn-sm"
            disabled={proposta.draft?.clienteMancante}
            title={proposta.draft?.clienteMancante ? 'Collega prima il cliente in anagrafica' : undefined}
            onClick={() => openModal('newProject', {
              dealId: deal.id,
              dealTitle: deal.title,
              clientId: proposta.draft?.clientId ?? undefined,
              title: proposta.draft?.name,
              value: proposta.draft?.revenue,
              deadline: proposta.draft?.deadline ?? undefined,
              canone: proposta.canone ?? undefined,
            })}
          >
            <Icon name="cube" size={12} /> Apri il progetto
          </button>
        )}
        {canWrite && proposta.azione === 'RINNOVO' && proposta.contratto && (
          <div className="relative">
            <button className="btn btn-sm" disabled={renew.isPending} onClick={() => setRenewing(true)}>
              <Icon name="repeat" size={12} /> Prolunga il canone
            </button>
            {renewing && (
              <RenewPopover
                contratto={proposta.contratto}
                pending={renew.isPending}
                onClose={() => setRenewing(false)}
                onConfirm={(v) => {
                  setRenewing(false);
                  renew.mutate({ id: deal.id, contratto: proposta.contratto!.name, ...v });
                }}
              />
            )}
          </div>
        )}
      </div>

      <div className="text-[13px] leading-[1.55]">{proposta.motivo}</div>

      {proposta.azione === 'PROGETTO' && proposta.draft?.clienteMancante && (
        <div className="adm-note mt-2">
          <Icon name="alert" size={12} className="mute" />
          <span>Non c'è ancora un cliente in anagrafica: scegli "Cliente in anagrafica" qui sotto, altrimenti il progetto non si può fatturare.</span>
        </div>
      )}

      {proposta.azione === 'PROGETTO' && proposta.canone && (
        <div className="adm-note mt-2">
          <Icon name="repeat" size={12} className="mute" />
          <span>
            Nascono <strong>due</strong> cose: il progetto da € {fmtEur(proposta.valore, true)} e il canone di gestione
            pratica, € {fmtEur(proposta.canone.amountNet)} al mese per {proposta.canone.mesi} mesi
            (€ {fmtEur(proposta.canone.totale, true)}). Il canone va nei ricavi ricorrenti, non dentro il progetto.
          </span>
        </div>
      )}

      {proposta.azione === 'RINNOVO' && proposta.contratto && (
        <div className="ed-kv mt-1">
          <KvRow label="Vale oggi" hint={proposta.contratto.endDate ? `scade il ${fmtDate(proposta.contratto.endDate)}` : 'senza scadenza'}>
            <span className="mono">€ {fmtEur(proposta.contratto.amountNet, true)} · € {fmtEur(proposta.contratto.annuo, true)} all'anno</span>
          </KvRow>
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Valore: sul voucher si sceglie la configurazione e il conto si vede
// ════════════════════════════════════════════════════════════════════
function ValoreBlock({ deal, canWrite, onPatch }: { deal: DealFull; canWrite: boolean; onPatch: (d: Record<string, unknown>) => void }) {
  if (!deal.isVoucher) {
    return (
      <>
        <div className="data-hd"><span className="t-micro">Valore</span></div>
        <div className="ed-kv">
          <KvRow label="Importo" hint="quanto ci aspettiamo di fatturare">
            {canWrite ? (
              <input
                className="inp tk-inline mono"
                type="number"
                min={0}
                step={100}
                style={{ width: 150 }}
                defaultValue={deal.amount}
                key={deal.amount}
                onBlur={(e) => { const v = Number(e.target.value); if (v !== deal.amount) onPatch({ amount: v }); }}
              />
            ) : `€ ${fmtEur(deal.amount, true)}`}
          </KvRow>
        </div>
      </>
    );
  }

  const successFee = deal.contributo != null ? deal.contributo * SUCCESS_FEE_RATE : null;
  const canone = CANONE_MENSILE * deal.mesiCanone;
  return (
    <>
      <div className="data-hd"><span className="t-micro">Quanto vale</span></div>
      <div className="ed-kv">
        <KvRow label="Imponibile progetto" hint="quanto fatturiamo noi: il bando ne rimborsa metà al cliente">
          {canWrite ? (
            <input
              className="inp tk-inline mono"
              type="number"
              min={0}
              step={1000}
              style={{ width: 150 }}
              defaultValue={deal.value}
              key={deal.value}
              onBlur={(e) => { const v = Number(e.target.value); if (v !== deal.value) onPatch({ amount: v }); }}
            />
          ) : `€ ${fmtEur(deal.value, true)}`}
        </KvRow>
        <KvRow label="Configurazione" hint="le due taglie tipiche del bando: scegliendone una l'imponibile si compila da sé">
          {canWrite ? (
            <select
              className="inp tk-inline"
              value={deal.scenario ?? ''}
              onChange={(e) => {
                const s = e.target.value as ScenarioKey | '';
                onPatch(s ? { scenario: s, amount: SPESA[s] } : { scenario: null });
              }}
            >
              <option value="">Su misura</option>
              {SCENARI.map((s) => <option key={s} value={s}>{SCENARIO_LABEL[s]}</option>)}
            </select>
          ) : (deal.scenario ? SCENARIO_LABEL[deal.scenario] : 'Su misura')}
        </KvRow>
        <KvRow label="Mesi di canone" hint={`${CANONE_MENSILE} € al mese`}>
          {canWrite ? (
            <input
              className="inp tk-inline mono"
              type="number"
              min={0}
              max={120}
              style={{ width: 90 }}
              defaultValue={deal.mesiCanone}
              key={deal.mesiCanone}
              onBlur={(e) => { const v = Number(e.target.value); if (v !== deal.mesiCanone) onPatch({ mesiCanone: v }); }}
            />
          ) : deal.mesiCanone}
        </KvRow>
      </div>

      {deal.value > 0 ? (
        <div className="ed-kv mt-1">
          <KvRow label="Contributo del bando" hint="metà della spesa, non oltre 20.000: è quanto NON paga il cliente, non un nostro ricavo">
            <span className="mono mute">€ {fmtEur(deal.contributo ?? 0, true)}</span>
          </KvRow>
          <KvRow label="Di tasca del cliente">
            <span className="mono mute">€ {fmtEur(deal.value - (deal.contributo ?? 0), true)}</span>
          </KvRow>
          <KvRow
            label="Gestione pratica"
            hint={`a parte, sopra il progetto: istruttoria € ${fmtEur(FEE_ISTRUTTORIA)} + success fee € ${fmtEur(successFee ?? 0)} + canone € ${fmtEur(canone)}`}
          >
            <span className="mono">€ {fmtEur(deal.feeGestione ?? 0, true)}</span>
          </KvRow>
        </div>
      ) : (
        <div className="mute text-[13px] mt-1">
          Finché non si scrive l'imponibile del progetto questa trattativa non entra nei totali dei periodi.
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Contatto: chi si chiama e dove
// ════════════════════════════════════════════════════════════════════
function ContattoBlock({ deal, canWrite, onPatch, clients }: {
  deal: DealFull;
  canWrite: boolean;
  onPatch: (d: Record<string, unknown>) => void;
  clients: Array<{ id: string; shortName: string }>;
}) {
  return (
    <>
      <div className="data-hd"><span className="t-micro">Contatto</span></div>
      <div className="ed-kv">
        <KvRow label="Azienda">
          {canWrite
            ? <TextField value={deal.companyName} placeholder="Ragione sociale" onSave={(v) => onPatch({ companyName: v })} />
            : (deal.companyName ?? '—')}
        </KvRow>
        <KvRow label="Persona">
          {canWrite
            ? <TextField value={deal.contactName} placeholder="Nome e cognome" onSave={(v) => onPatch({ contactName: v })} />
            : (deal.contactName ?? '—')}
        </KvRow>
        <KvRow label="Telefono">
          {canWrite
            ? <TextField value={deal.phone} placeholder="Numero da chiamare" mono onSave={(v) => onPatch({ phone: v })} />
            : (deal.phone ? <a className="hover:underline" href={`tel:${deal.phone}`}>{deal.phone}</a> : '—')}
        </KvRow>
        <KvRow label="Email">
          {canWrite
            ? <TextField value={deal.email} placeholder="indirizzo@azienda.it" onSave={(v) => onPatch({ email: v })} />
            : (deal.email ? <a className="hover:underline" href={`mailto:${deal.email}`}>{deal.email}</a> : '—')}
        </KvRow>
        <KvRow label="Cliente in anagrafica" hint="se è già dei nostri">
          {canWrite ? (
            <select className="inp tk-inline" value={deal.clientId ?? ''} onChange={(e) => onPatch({ clientId: e.target.value || null })}>
              <option value="">Non ancora cliente</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.shortName}</option>)}
            </select>
          ) : (deal.client?.shortName ?? 'Non ancora cliente')}
        </KvRow>
        {deal.ghlContactId && (
          <KvRow label="Contatto GoDeal"><span className="mono mute text-[13px]">{deal.ghlContactId}</span></KvRow>
        )}
      </div>
      <NoteBlock note={deal.note} canWrite={canWrite} onSave={(note) => onPatch({ note })} />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Storico: cosa è successo e quando
// ════════════════════════════════════════════════════════════════════
function StoricoBlock({ deal, canWrite, onAdd, pending }: {
  deal: DealFull;
  canWrite: boolean;
  onAdd: (text: string) => void;
  pending: boolean;
}) {
  const [text, setText] = useState('');
  const submit = () => {
    const t = text.trim();
    if (!t || pending) return;
    onAdd(t);
    setText('');
  };
  return (
    <div>
      <div className="data-hd"><span className="t-micro">Storico</span></div>
      {canWrite && (
        <form className="row mb-3" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <input
            className="inp"
            placeholder="Cos'è successo: una chiamata, una risposta, un documento…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Aggiungi al diario"
          />
          <button className="btn btn-sm" type="submit" disabled={!text.trim() || pending}>Aggiungi</button>
        </form>
      )}
      {deal.events.length ? (
        <div className="flex flex-col">
          {deal.events.map((e) => (
            <div key={e.id} className="row between py-1.5 border-b border-line last:border-0" style={{ gap: 12 }}>
              <div className="min-w-0">
                <span className="text-[13px]">{e.text}</span>
                {e.createdBy && <span className="mute text-[12px]"> · {e.createdBy.firstName}</span>}
              </div>
              <span className="mono text-[12px] mute-2 shrink-0">{fmtDate(e.createdAt)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mute text-[13px]">Ancora niente.</div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Pezzi di form
// ════════════════════════════════════════════════════════════════════
function TitleEditor({ title, canWrite, onSave }: { title: string; canWrite: boolean; onSave: (t: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  useEffect(() => { setValue(title); }, [title]);

  const commit = () => {
    setEditing(false);
    const v = value.trim();
    if (!v) { setValue(title); return; }
    if (v !== title) onSave(v);
  };

  if (editing) {
    return (
      <input
        className="ed-title tk-title-input"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setValue(title); setEditing(false); }
        }}
        aria-label="Nome della trattativa"
      />
    );
  }
  return (
    <h1
      className={`ed-title${canWrite ? ' tk-title-editable' : ''}`}
      title={canWrite ? 'Clicca per modificare' : undefined}
      onClick={() => { if (canWrite) setEditing(true); }}
    >
      {title}
    </h1>
  );
}

/** Campo di testo che salva all'uscita: niente pulsante Salva per ogni riga. */
function TextField({ value, placeholder, mono, onSave }: {
  value: string | null;
  placeholder: string;
  mono?: boolean;
  onSave: (v: string | null) => void;
}) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  return (
    <input
      className={`inp tk-inline${mono ? ' mono' : ''}`}
      placeholder={placeholder}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { const t = v.trim(); if (t !== (value ?? '')) onSave(t || null); }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
    />
  );
}

function KvRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="ed-kv-row">
      <div className="k">{label}{hint && <div className="tk-kv-hint">{hint}</div>}</div>
      <div className="v">{children}</div>
    </div>
  );
}

function NoteBlock({ note, canWrite, onSave }: { note: string | null; canWrite: boolean; onSave: (v: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  return (
    <div className="mt-3">
      <div className="data-hd">
        <span className="t-micro">Note <span className="mute-2 normal-case tracking-normal font-medium">· facoltative</span></span>
        {canWrite && !open && (
          <button className="btn btn-sm btn-ghost" onClick={() => { setText(note ?? ''); setOpen(true); }}>
            <Icon name={note ? 'file' : 'plus'} size={12} /> {note ? 'Modifica' : 'Aggiungi'}
          </button>
        )}
      </div>
      {open ? (
        <>
          <textarea className="inp" rows={3} autoFocus placeholder="Quello che serve sapere prima di chiamare" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex gap-2 mt-2">
            <button className="btn btn-sm" onClick={() => { onSave(text.trim() || null); setOpen(false); }}>Salva</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>Annulla</button>
          </div>
        </>
      ) : note ? (
        <div className="whitespace-pre-wrap leading-relaxed">{note}</div>
      ) : (
        <div className="mute text-[13px]">Nessuna nota.</div>
      )}
    </div>
  );
}

export type { StageLite };
